import { createApi, type FetchArgs } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from 'src/app-redux/rtkq.interceptor';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import type { Configuration, EConfigName, TAddConfig, TConfig } from 'src/configuration/Configuration';
import { EmailNotifications, EmailNotificationsEnum } from 'src/configuration/EmailNotifications';

const configTag = 'config';
const oldConfigTag = 'oldConfig';

/** RTK Query API slice for configuration management endpoints. */
export const configApi = createApi({
  reducerPath: 'configApi',
  // reducerPath: configTag + 'Api',
  tagTypes: [configTag, oldConfigTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch a configuration by its name. */
    fetchConfigByName: builder.query<Configuration, string>({
      providesTags: [oldConfigTag],
      query: (configName) => `${apiResources.config}/name/${configName}`,
      ...transformResponses,
    }),
    /** Mutation to update a configuration by ID and value. */
    updateConfig: builder.mutation<void, { id: number; value: number; userId: number }>({
      query: (body) => ({ url: apiResources.config, method: 'PUT', body }),
      invalidatesTags: (_result, error) => (error ? [] : [oldConfigTag]),
    }),
    /** Query to fetch all configurations for a user. */
    getAllConfigs: builder.query<Array<TConfig>, { userId: number; token?: string }>({
      providesTags: [configTag],
      query: ({ userId, token }) => {
        const payload: FetchArgs = { url: `${apiResources.config}/v2/${userId}` };
        if (token)
          payload.headers = {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
          };
        return payload;
      },
      ...transformResponses,
    }),
    /** Mutation to create new configurations for a user. */
    createConfigs: builder.mutation<string, { userId: number; configs: Array<TAddConfig> }>({
      query: ({ userId, configs }) => ({ url: `${apiResources.config}/v2/${userId}`, method: 'POST', body: configs }),
      invalidatesTags: (_result, error) => (error ? [] : [configTag]),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        /**
         * Reload the page to load latest preferences into local storage.
         */
        window.location.reload();
      },
      ...transformResponses,
    }),
    /** Mutation to delete a configuration by name. */
    deleteConfig: builder.mutation<string, EConfigName>({
      query: (configName) => ({ url: `${apiResources.config}?configName=${configName}`, method: 'DELETE' }),
      invalidatesTags: (_result, error) => (error ? [] : [configTag]),
      ...transformResponses,
    }),
    /** Query to fetch email notification settings. */
    fetchEmailNotifications: builder.query<EmailNotifications, void>({
      query: () => apiResources.shipperConfig,
      ...transformResponses,
    }),
    /** Mutation to update email notification settings. */
    updateEmailNotifications: builder.mutation<void, EmailNotificationsEnum>({
      query: (action) => ({ url: `${apiResources.shipperConfig}?email_notifications=${action}`, method: 'POST' }),
      // invalidatesTags: (_result, error) => (error ? [] : [emailNotificationsTag]), // Not needed as we have added `Optimistic Update` logic below
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          configApi.util.updateQueryData('fetchEmailNotifications', undefined, (draft) => {
            // The `draft` is Immer-wrapped and can be "mutated" like in createSlice
            draft.email_notifications = arg; // update our copy of data
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFetchConfigByNameQuery,
  useUpdateConfigMutation,
  useFetchEmailNotificationsQuery,
  useUpdateEmailNotificationsMutation,
  useGetAllConfigsQuery,
  useCreateConfigsMutation,
  useDeleteConfigMutation,
} = configApi;
